import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Grid,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { useStyles } from '../../../styles';
import { SessionsImagesFileUpload } from './SessionImagesFileUpload/SessionImagesFileUpload.component';
import { SessionImagesFileUploadList } from './SessionImagesFileUploadList/SessionImagesFileUploadList.component';
import {
  deleteSheetPage,
  getExamSheetThumbnail,
  getUploadedExamSheetFiles,
} from '../../../../services/exams/sessions/sessions.service';
import { AxiosResponse } from 'axios';
import Miniature from '../../../../assets/images/miniatureSheet.png';
import {
  DeleteState,
  DownloadedFile,
  SessionsImagesManagmentProps,
  ExamSheetFile,
} from './SessionImagesManagment.types';
import { useLayoutContext } from '../../../../Context/Layout.context';
import DeleteIcon from '@mui/icons-material/Delete';
import { Prompt } from '../../../../components/Prompt/Prompt.component';

export const SessionsImagesManagment: React.FC<SessionsImagesManagmentProps> = ({ sheetId, isConfigured }) => {
  const classes = useStyles();
  const [files, setFiles] = useState<[]>([]);
  const [examSheets, setExamSheets] = useState<ExamSheetFile[]>([]);
  const { showLoader } = useLayoutContext();
  const [selected, setSelected] = React.useState<number[]>([]);
  const [isPromptOpen, setIsPromptOpen] = React.useState<boolean>(false);
  const [isErrorPromptOpen, setIsErrorPromptOpen] = React.useState<boolean>(false);
  const [thumbnailSrc, setThumbnailSrc] = React.useState<string>('');
  const [isThumbnailVisible, setIsThumbnailVisible] = React.useState<boolean>(false);
  const [isAllChecked, setIsAllChecked] = React.useState<boolean>(false);
  const abortController = useRef<AbortController | null>(null);
  const { openAlert } = useLayoutContext();

  const refreshData = () => {
    showLoader(true, 'Pobieram dane');
    getUploadedExamSheetFiles(sheetId)
      .then((response: AxiosResponse<ExamSheetFile[]>) => {
        setExamSheets(response.data);
      })
      .catch(() => {
        console.error('error');
      })
      .finally(() => {
        showLoader(false);
      });
  };

  useEffect(() => {
    abortController.current = new AbortController();
    refreshData();
  }, []);

  const onAbort = () => {
    abortController.current?.abort();
    if (abortController?.current?.signal?.aborted) abortController.current = new AbortController();
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = examSheets.map((n: DownloadedFile) => n.pageNumber);
      setSelected(newSelecteds);
      setIsThumbnailVisible(false);
      setIsAllChecked(true);
      return;
    }
    setIsAllChecked(false);
    setSelected([]);
  };

  const handleClick = (name: number) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }

    setSelected(newSelected);
    setIsThumbnailVisible(false);
  };

  const handleDeleteSheetPage = (sheetId: string, pageList: number[]) => {
    deleteSheetPage(sheetId, pageList).then(
      (response) => {
        if (response.data === DeleteState.SUCCESS) {
          setIsPromptOpen(false);
          setSelected([]);
          setIsThumbnailVisible(false);
          openAlert(
            'success',
            selected.length === 1
              ? 'Strona została pomyślnie usunięta'
              : `${selected.length} z ${examSheets.length} stron zostało usuniętych pomyślnie`,
          );
          refreshData();
        } else if (response.data === DeleteState.ALREADYCONFIGURED) {
          setIsPromptOpen(false);
          setIsErrorPromptOpen(true);
        }
      },
      () => {
        setIsPromptOpen(false);
        setSelected([]);
        setIsThumbnailVisible(false);
        if (!navigator.onLine) {
          openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
        } else {
          openAlert(
            'error',
            selected.length === 1
              ? 'Usunięcie strony nie powiodło się, spróbuj ponownie później'
              : `Usunięcie ${selected.length} z ${examSheets.length} stron nie powiodło się, spróbuj ponownie później`,
          );
        }
      },
    );
  };

  const showThumbnail = async (sheetId: string, pageNumber: number) => {
    const response = await getExamSheetThumbnail(sheetId, pageNumber);
    if (response) {
      const base64 = window.btoa(
        new Uint8Array(response.data).reduce(function (data, byte) {
          return data + String.fromCharCode(byte);
        }, ''),
      );
      setThumbnailSrc(base64);
      setIsThumbnailVisible(true);
    }
  };

  const handleCheckboxClick = (sheetId: string, pageNumber: number) => {
    handleClick(pageNumber);
    if (!isAllChecked) {
      showThumbnail(sheetId, pageNumber);
    }
  };

  const showFirstSelectedImage = (sheetId: string, pageNumber: number) => {
    handleClick(pageNumber);
    if (selected.length > 1 && !isAllChecked) {
      showThumbnail(sheetId, selected[0]);
    }
  };

  const isSelected = (name: number) => selected.indexOf(name) !== -1;

  return (
    <Grid container>
      <div style={{ display: 'flex', width: '100%' }}>
        {examSheets.length > 0 && (
          <div style={{ width: '100%' }}>
            <div style={{ width: '100%', display: 'inline-flex', justifyContent: 'space-between' }}>
              <Typography className={classes.title}>Wzorcowy arkusz</Typography>
              <SessionsImagesFileUpload
                files={files}
                setFiles={setFiles}
                examSheetId={sheetId}
                variant="green"
                isConfigured={isConfigured}
              />
            </div>
            <TableHead className={classes.noBorderRow}>
              <TableRow style={{ height: '50px' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    disableRipple
                    color="primary"
                    onChange={handleSelectAllClick}
                    inputProps={{ 'aria-label': 'Zaznacz wszystkie' }}
                  />
                </TableCell>
                <TableCell style={{ height: '50px' }}>
                  {selected.length > 0 && (
                    <div
                      style={{
                        width: '250px',
                        display: 'inline-flex',
                        justifyContent: 'space-around',
                        alignItems: 'end',
                      }}
                    >
                      <Typography style={{ fontSize: '0.9em' }}>
                        Zaznaczono {selected.length} z {examSheets.length}
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        style={{ color: 'red', borderColor: 'red' }}
                        onClick={() => setIsPromptOpen(true)}
                      >
                        Usuń
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <div style={isThumbnailVisible ? { display: 'inline-flex', paddingRight: '20px', width: '100%' } : {}}>
              <TableContainer
                style={
                  isThumbnailVisible
                    ? { width: '60%', height: '500px', overflow: 'auto' }
                    : { width: '100%', height: '500px', overflow: 'auto' }
                }
              >
                <TableBody>
                  {examSheets
                    .sort((a: ExamSheetFile, b: ExamSheetFile) => a.pageNumber - b.pageNumber)
                    .map((file: ExamSheetFile) => {
                      const isItemSelected = isSelected(file.pageNumber);
                      return (
                        <TableRow
                          key={file.sheetPageId}
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          selected={isItemSelected}
                          onClick={
                            !isItemSelected
                              ? () => handleCheckboxClick(sheetId, file.pageNumber)
                              : () => showFirstSelectedImage(sheetId, file.pageNumber)
                          }
                          className={classes.selectedRowCustom}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox disableRipple color="primary" checked={isItemSelected} />
                          </TableCell>
                          <TableCell>
                            <img src={Miniature} alt="miniaturka arkusza wzorcowego" style={{ marginRight: '10px' }} />
                          </TableCell>
                          <TableCell id={file.sheetPageId} style={{ width: '100%' }}>
                            {file.displayName}
                            <p style={{ margin: '0' }}>PNG</p>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </TableContainer>
              {isThumbnailVisible && (
                <div style={{ width: '45%', height: '500px' }}>
                  <img
                    style={{ height: '95%', width: '95%', border: '15px solid rgba(202, 202, 252, 0.3)' }}
                    src={`data:;base64,${thumbnailSrc}`}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {examSheets.length === 0 && (
          <div style={{ display: 'flex' }}>
            <Typography className={classes.title}>Wzorcowy arkusz</Typography>
            <div style={{ marginTop: '150px', textAlign: 'center', width: '500px' }}>
              <PhotoLibraryOutlinedIcon
                style={{ fontSize: '80px', color: 'rgba(202, 202, 252, 0.3', margin: '0 auto' }}
              />
              <Typography className={classes.subtitleMediumViolet}>
                W tej chwili nie został zaimportowany żaden wzorcowy arkusz
              </Typography>
              <Typography style={{ marginTop: '20px' }}>
                Pliki arkusza powinny mieć nazwę zaczynającą się od liczby
              </Typography>
              <SessionsImagesFileUpload
                files={files}
                setFiles={setFiles}
                examSheetId={sheetId}
                variant="blue"
                isConfigured={isConfigured}
                abortController={abortController}
              />
            </div>
          </div>
        )}

        <SessionImagesFileUploadList
          files={files}
          open={true}
          refreshData={() => refreshData()}
          sheetId={sheetId}
          onAbort={onAbort}
        />

        <Prompt
          type="confirmation"
          open={isPromptOpen}
          size="xs"
          onConfirm={() => handleDeleteSheetPage(sheetId, selected)}
          onCancel={() => setIsPromptOpen(false)}
          mainActionButtonText="TAK"
          mainText={
            selected.length === examSheets.length
              ? 'Czy na pewno chcesz usunąć wszystkie strony'
              : '' || selected.length === 1
              ? `Czy na pewno chcesz usunąć ${selected.length} stronę`
              : `Czy na pewno chcesz usunąć ${selected.length} strony`
          }
        />
        <Prompt
          type="error"
          open={isErrorPromptOpen}
          size="xs"
          onConfirm={() => setIsErrorPromptOpen(false)}
          mainActionButtonText="POWRÓT"
          mainText="Nie można usunąć stron arkusza wzorcowego ponieważ arkusz został skonfigurowany."
        />
      </div>
    </Grid>
  );
};
